import { useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useI18n } from "@/lib/i18n";
import { Menu, X } from "lucide-react";

export default function Header() {
  const { lang, t } = useI18n();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const base = `/${lang}`;

  const links = [
    { to: `${base}`, hash: "", label: t("nav.home"), end: true, testId: "nav-link-home" },
    { to: `${base}`, hash: "about", label: t("nav.about"), end: false, testId: "nav-link-about" },
    { to: `${base}`, hash: "how-it-works", label: t("nav.howItWorks"), end: false, testId: "nav-link-how" },
    { to: `${base}`, hash: "newsletter", label: t("nav.newsletter"), end: false, testId: "nav-link-newsletter" },
  ];

  const otherLang = lang === "ar" ? "en" : "ar";
  const switchLang = () => {
    const currentPath = location.pathname;
    const rest = currentPath.replace(/^\/(ar|en)/, "");
    const target = `/${otherLang}${rest || ""}` + location.hash;
    navigate(target, { replace: false });
    setOpen(false);
  };

  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleNavClick = (e, link) => {
    e.preventDefault();
    setOpen(false);
    const onHome = location.pathname === base || location.pathname === `${base}/`;

    if (!link.hash) {
      // Home link
      if (onHome) {
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        navigate(base);
      }
      return;
    }

    if (onHome) {
      scrollToId(link.hash);
    } else {
      // Navigate home first, then scroll after mount
      navigate(base, { state: { scrollTo: link.hash } });
    }
  };

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 w-full bg-[#0D1B2A] text-white border-b border-white/10"
    >
      <div className="mx-auto max-w-7xl px-5 md:px-8 h-16 md:h-20 flex items-center justify-between">
        <Link
          to={base}
          data-testid="header-logo"
          className="flex items-center gap-3 no-underline text-white"
          onClick={() => setOpen(false)}
        >
          <span
            aria-hidden="true"
            className="inline-block h-8 w-8"
            style={{
              background:
                "linear-gradient(180deg, #D4AF37 0%, #D4AF37 40%, #E0E1DD 40%, #E0E1DD 100%)",
            }}
          />
          <span
            className="font-serif text-xl md:text-2xl tracking-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {t("footer.brand")}
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8" aria-label="primary">
          {links.map((l) => (
            <NavLink
              key={l.testId}
              to={l.hash ? `${l.to}#${l.hash}` : l.to}
              end={l.end}
              data-testid={l.testId}
              onClick={(e) => handleNavClick(e, l)}
              className={({ isActive }) =>
                `text-sm font-medium no-underline text-white/85 hover:text-[#D4AF37] ${
                  isActive && l.end ? "text-[#D4AF37]" : ""
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <button
            type="button"
            onClick={switchLang}
            data-testid="lang-switcher-desktop"
            aria-label={lang === "ar" ? t("language.switchToEn") : t("language.switchToEs")}
            className="ml-2 inline-flex items-center gap-2 border border-white/25 px-3 py-1.5 text-xs font-semibold uppercase tracking-widest text-white hover:bg-white hover:text-[#0D1B2A]"
          >
            <span className={lang === "ar" ? "text-[#D4AF37]" : ""}>{t("language.es")}</span>
            <span className="text-white/40">/</span>
            <span className={lang === "en" ? "text-[#D4AF37]" : ""}>{t("language.en")}</span>
          </button>
        </nav>

        <button
          type="button"
          className="md:hidden inline-flex items-center justify-center h-10 w-10 border border-white/25 text-white"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? t("nav.closeMenu") : t("nav.openMenu")}
          data-testid="hamburger-toggle"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {open && (
        <div
          className="md:hidden bg-[#0D1B2A] border-t border-white/10"
          data-testid="mobile-menu"
        >
          <nav className="px-5 py-4 flex flex-col gap-1" aria-label="mobile">
            {links.map((l) => (
              <a
                key={l.testId + "-m"}
                href={l.hash ? `${l.to}#${l.hash}` : l.to}
                data-testid={l.testId + "-mobile"}
                onClick={(e) => handleNavClick(e, l)}
                className="px-2 py-3 text-white/90 no-underline border-b border-white/10 text-base"
              >
                {l.label}
              </a>
            ))}
            <button
              type="button"
              onClick={switchLang}
              data-testid="lang-switcher-mobile"
              className="mt-3 self-start inline-flex items-center gap-2 border border-white/25 px-3 py-2 text-xs font-semibold uppercase tracking-widest text-white"
            >
              <span className={lang === "ar" ? "text-[#D4AF37]" : ""}>{t("language.es")}</span>
              <span className="text-white/40">/</span>
              <span className={lang === "en" ? "text-[#D4AF37]" : ""}>{t("language.en")}</span>
            </button>
          </nav>
        </div>
      )}
    </header>
  );
}
